import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { MessageContent, useMessageContext } from "./message"
import useMessage from "@/components/ui/message/useMessage"
import MessageHelpers from "@/lib/messageHelpers"
import css from "./message-text.module.css"
import { DriverContext } from "@/contexts/DriverContext"
import { Element } from "@xmpp/xml"
import { Interweave } from "interweave"
import { UrlMatcher } from "interweave-autolink"
import { GoogleMapsMatcher } from "@/lib/GoogleMapsMatcher"
import { SlickShiftShareRouteAppMatcher } from "@/lib/SlickShiftShareRouteAppMatcher"
import { SlickShiftShareRouteHereWeGoMatcher } from "@/lib/SlickShiftShareRouteHereWeGoMatcher"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { FileText } from "lucide-react"
import { useAppDispatch } from "@/reducers/hooks"
import { updateImageGallery, updatePDFViewer } from "@/reducers/overlaysSlice"
import { Message } from "@/reducers/messagesSlice"
import { SearchHighlightMatcher } from "@/lib/SearchHighlightMatcher"

const isPDF = (url: string) => /\.pdf$/i.test(url)

export interface MessageTextProps extends React.HTMLAttributes<HTMLDivElement> {
  content?: string
  contentOffset?: number
  highlightTerms?: string[]
  forwarded?: boolean
  reply?: boolean
}

const MessageText = React.forwardRef<HTMLDivElement, MessageTextProps>(
  (
    {
      className,
      forwarded = false,
      reply = true,
      children,
      highlightTerms,
      content,
      contentOffset,
      ...props
    },
    ref,
  ) => {
    const message = useMessage({ forwarded, reply })
    const { interactive } = useMessageContext()

    const bodyRef = useRef<HTMLDivElement>(null)

    const { addRouteStop, routeJid, highlightDuplicateStop } =
      useContext(DriverContext)

    const [processingAddresses, setProcessingAddresses] = useState<string[]>([])

    const onAddressClick = useCallback(
      async (e: any) => {
        e.preventDefault()
        const address = (e as React.MouseEvent).currentTarget.textContent

        if (address && processingAddresses.includes(address)) {
          return
        }

        if (address && routeJid && addRouteStop) {
          setProcessingAddresses((prev) => [...prev, address])

          const safetyTimeoutId = setTimeout(() => {
            setProcessingAddresses((prev) => prev.filter((a) => a !== address))
          }, 10000)

          try {
            const result = await addRouteStop(address, routeJid, message.id)
            clearTimeout(safetyTimeoutId)
            setProcessingAddresses((prev) => prev.filter((a) => a !== address))
            let duplicateId
            if ("getChild" in result) {
              duplicateId = (result as unknown as Element)
                .getChild("command")
                ?.getChildByAttr(
                  "var",
                  "duplicate-stop-id",
                  "jabber:x:data",
                  true,
                )
                ?.getChildText("value")
            }
            if (duplicateId && highlightDuplicateStop.current) {
              highlightDuplicateStop.current(duplicateId)
            }
          } catch (e) {
            clearTimeout(safetyTimeoutId)
            setProcessingAddresses((prev) => prev.filter((a) => a !== address))
          }
        }

        window.analytics.track("AddressLinkClick")
      },
      [
        addRouteStop,
        routeJid,
        message.id,
        processingAddresses,
        highlightDuplicateStop,
      ],
    )

    /**
     * Attach native click listeners to address links injected by Interweave.
     *
     * Interweave renders raw HTML into the DOM, so React `onClick` handlers on the
     * original JSX won't be attached to those generated anchor (`A`) elements.
     * This effect finds elements with class `css.address` inside `bodyRef` and
     * registers a native click listener that delegates to `onAddressClick`.
     *
     * The effect re-runs when `onAddressClick` changes so newly inserted/removed
     * links stay in sync. The cleanup removes listeners from the previously found
     * set of links.
     */

    useEffect(() => {
      const currentBodyRef = bodyRef.current
      if (!currentBodyRef || !interactive) return

      const links = currentBodyRef.getElementsByClassName(css.address)

      Array.prototype.forEach.call(links, (link: HTMLElement) => {
        if (link.nodeName === "A") {
          link.addEventListener("click", onAddressClick)
        }
      })

      return () => {
        Array.prototype.forEach.call(links, (link: HTMLElement) => {
          if (link.nodeName === "A") {
            link.removeEventListener("click", onAddressClick)
          }
        })
      }
    }, [interactive, onAddressClick])

    const bodyWithAppliedReferences = useCallback(() => {
      const messageForDisplay =
        content !== undefined && contentOffset !== undefined
          ? {
              ...message,
              body: content,
              references: message.references
                ?.map((reference) => ({
                  ...reference,
                  begin: reference.begin - contentOffset,
                  end: reference.end - contentOffset,
                }))
                .filter(
                  (reference) =>
                    reference.begin >= 0 && reference.end <= content.length,
                ),
            }
          : message

      return MessageHelpers.getBodyWithAppliedAddressReferences(
        messageForDisplay,
        (address) => {
          // Check if this address is being processed
          const isProcessing = processingAddresses.includes(address)
          return `<a class="${css.address} ${isProcessing ? "text-shimmer" : ""}">${address}</a>`
        },
      )
    }, [message, processingAddresses, content, contentOffset])

    /**
     * Synchronize processing state CSS on address links.
     *
     * - Runs when `processingAddresses` changes.
     * - Finds all elements with class `css.address` inside `bodyRef`.
     * - For each anchor (`A`) element, checks its text content and toggles
     *   the `css.addressProcessing` class depending on whether that address
     *   is currently being processed (`processingAddresses.includes(address)`).
     * - No-op if `bodyRef` is not mounted.
     */

    useEffect(() => {
      const currentBodyRef = bodyRef.current
      if (!currentBodyRef) return

      const links = currentBodyRef.getElementsByClassName(css.address)

      Array.prototype.forEach.call(links, (link: HTMLElement) => {
        if (link.nodeName === "A") {
          const address = link.textContent
          if (address && processingAddresses.includes(address)) {
            link.classList.add("text-shimmer")
          } else {
            link.classList.remove("text-shimmer")
          }
        }
      })
    }, [processingAddresses])

    if (message.cmrLinkRequest?.fileUrl) {
      return (
        <CmrLinkRequestCard
          message={message}
          ref={ref}
          className={className}
          {...props}
        >
          {children}
        </CmrLinkRequestCard>
      )
    }

    return (
      <MessageContent ref={ref} className={cn("text-sm", className)} {...props}>
        <div
          className="whitespace-pre-wrap break-words"
          ref={bodyRef}
          data-testid="message-body"
        >
          <Interweave
            content={
              content !== undefined && contentOffset === undefined
                ? content
                : bodyWithAppliedReferences()
            }
            newWindow
            matchers={[
              new SlickShiftShareRouteAppMatcher("slickshift"),
              new SlickShiftShareRouteHereWeGoMatcher("herewego"),
              new GoogleMapsMatcher("googlemaps"),
              new UrlMatcher("url"),
              ...(highlightTerms?.length
                ? [new SearchHighlightMatcher(highlightTerms)]
                : []),
            ]}
          />
        </div>
        {children}
      </MessageContent>
    )
  },
)

MessageText.displayName = "MessageText"

const CmrLinkRequestCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { message: Message }
>(({ message, className, children, ...props }, ref) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const req = message.cmrLinkRequest!
  const mappedUrl = mapFileUrlToSameOrigin(req.fileUrl)
  const pdf = isPDF(req.fileUrl)

  const handleClick = useCallback(() => {
    if (pdf) {
      dispatch(
        updatePDFViewer({
          visible: true,
          PDFMessage: message,
        }),
      )
    } else {
      dispatch(
        updateImageGallery({
          visible: true,
          directImage: { src: mappedUrl },
        }),
      )
    }
  }, [dispatch, message, mappedUrl, pdf])

  return (
    <MessageContent ref={ref} className={cn("text-sm", className)} {...props}>
      <p className="mb-2">{t("chat.cmrLinkRequest.prompt")}</p>
      <button
        className="group flex items-center gap-2 rounded-md border border-border p-2 transition-colors hover:bg-muted"
        onClick={handleClick}
      >
        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded border bg-muted">
          {pdf ? (
            <div className="flex h-full w-full items-center justify-center bg-red-50 dark:bg-red-950">
              <FileText className="h-6 w-6 text-red-500" />
            </div>
          ) : (
            <img
              src={mappedUrl}
              alt={req.fileName || "CMR"}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          )}
        </div>
      </button>
      {children}
    </MessageContent>
  )
})
CmrLinkRequestCard.displayName = "CmrLinkRequestCard"

export { MessageText }
